import React, { useEffect, useState } from 'react';
import { Award, ChevronRight, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const downloadShareCertificate = async (token) => {
  const r = await fetch(`${API_URL}/api/dataroom/share-certificate`, { headers: { 'X-Dataroom-Token': token } });
  if (!r.ok) return false;
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'HiBFF-Investment-Certificate.pdf'; a.click();
  URL.revokeObjectURL(url);
  return true;
};

const UNLOCKED = [
  ['Timed access, watermarked, view-only', 'Permanent access — no expiry, no watermark'],
  ['Documents locked to the viewer', 'Board deck downloads unlocked'],
  ['Curated numbers only', 'KPI history, Fundraise Status & Investor Brief'],
  ['Generic contact footer', 'HiBFF Pulse — live-data emails to your inbox'],
];

export const DataRoomInvestorWelcome = ({ name, token, instrument, onEnter, staticMode = false }) => {
  const firstName = String(name || 'there').split(' ')[0];
  const [leaving, setLeaving] = useState(staticMode);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setShown(true); if (staticMode) setLeaving(false); }, 80);
    return () => clearTimeout(t);
  }, [staticMode]);

  const enter = () => {
    if (leaving && shown) return;
    setLeaving(true);
    if (!staticMode) {
      fetch(`${API_URL}/api/dataroom/investor-welcome/ack`, {
        method: 'POST', headers: { 'X-Dataroom-Token': token },
      }).catch(() => {});
    }
    setTimeout(onEnter, 1800);
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-start lg:items-center justify-center p-4 overflow-y-auto"
      style={{
        background: 'linear-gradient(150deg, #06251A 0%, #0B3B2A 55%, #123B2E 100%)',
        transform: leaving ? 'translateY(-100%)' : 'translateY(0)',
        opacity: shown ? 1 : 0,
        transition: 'transform 1.8s cubic-bezier(0.65, 0, 0.35, 1), opacity 0.6s ease',
      }}
      data-testid="dr-investor-welcome">
      <div className="max-w-5xl w-full py-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">

          {/* LEFT — Founder portrait video */}
          <div className="flex-shrink-0 mx-auto lg:mx-0 w-full max-w-[260px] sm:max-w-[300px] lg:max-w-[340px]" data-testid="dr-investor-video">
            <div className="rounded-2xl overflow-hidden border border-[#D4AF37]/40 shadow-2xl">
              <video controls playsInline preload="metadata" poster={`${API_URL}/api/static/brand/investor_welcome_poster.jpg`} className="w-full block" style={{ aspectRatio: '9 / 16', objectFit: 'cover', background: '#031710' }}
                src={`${API_URL}/api/static/brand/investor_welcome_720p.mp4`}>
                Your browser does not support video playback.
              </video>
            </div>
            <p className="text-[10px] text-emerald-100/50 text-center mt-2 uppercase tracking-widest font-bold">A message from Robert</p>
          </div>

          {/* RIGHT — Welcome content */}
          <div className="flex-1 min-w-0 text-center lg:text-left">
        <div className="w-16 h-16 rounded-full mx-auto lg:mx-0 mb-5 flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D97A)' }}>
          <Award size={30} className="text-[#06251A]" />
        </div>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F5D97A] mb-3">Active Investor</p>
        <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-3">
          Welcome to the inside, {firstName}.
        </h1>
        <p className="text-sm text-emerald-100/80 mb-6">You're no longer looking through the glass.</p>

        {/* Investment plaque — SAFE/ASA convertible instrument, not shares */}
        <div className="rounded-2xl border border-[#D4AF37]/40 bg-white/5 backdrop-blur-sm p-6 mb-8" data-testid="dr-investor-plaque">
          <div className="grid grid-cols-3 gap-4">
            <div><p className="text-[9px] uppercase tracking-widest text-emerald-200/60 font-bold">Invested</p>
              <p className="text-xl font-black text-[#F5D97A] mt-1">${Number(instrument?.amount_usd || 0).toLocaleString()}</p></div>
            <div><p className="text-[9px] uppercase tracking-widest text-emerald-200/60 font-bold">Instrument</p>
              <p className="text-xl font-black text-white mt-1 uppercase">{instrument?.type || 'ASA'}</p></div>
            <div><p className="text-[9px] uppercase tracking-widest text-emerald-200/60 font-bold">{instrument?.discount_pct ? 'Discount' : 'Reference'}</p>
              {instrument?.discount_pct
                ? <p className="text-xl font-black text-white mt-1">{instrument.discount_pct}%</p>
                : <p className="text-sm font-black text-white mt-2 font-mono">{instrument?.reference || '—'}</p>}
            </div>
          </div>
          <p className="text-[11px] text-emerald-100/60 mt-3" data-testid="dr-instrument-note">
            Converts to equity at the next priced round or longstop date — no shares issued until conversion.
          </p>
          <button onClick={() => downloadShareCertificate(token)}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#D4AF37]/60 text-[#F5D97A] text-[11px] font-black hover:bg-[#D4AF37]/10 transition-colors"
            data-testid="dr-certificate-download-ceremony">
            <Download size={12} /> Download Your Investment Certificate
          </button>
        </div>

        {/* What just unlocked */}
        <div className="text-left rounded-2xl bg-white/5 border border-white/10 p-5 mb-8" data-testid="dr-investor-unlocked">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F5D97A] mb-3">What just unlocked</p>
          <div className="space-y-2">
            {UNLOCKED.map(([before, after], i) => (
              <div key={i} className="grid sm:grid-cols-2 gap-1 sm:gap-3 text-[12px] border-b border-white/5 pb-2">
                <span className="text-emerald-100/50 line-through decoration-emerald-100/30">{before}</span>
                <span className="text-white font-semibold">{after}</span>
              </div>
            ))}
          </div>
        </div>

        <button onClick={enter}
          className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-black text-sm text-[#06251A] shadow-xl hover:scale-[1.03] transition-transform"
          style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D97A)' }}
          data-testid="dr-investor-enter-btn">
          Enter Your Data Room <ChevronRight size={16} />
        </button>
        <p className="text-[11px] text-emerald-100/50 mt-5">Thank you for believing in HiBFF — Robert</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataRoomInvestorWelcome;
